import { Button, Card, Descriptions, Message, Popconfirm, Space, Table, Tag } from "@arco-design/web-react"
import { Link, useNavigate, useParams } from "react-router-dom";
import { WOPOAPI, WorkOrderAPI, WoFulfillmentAPI } from "@/apis/manufacturing";
import { Number } from "@/components/Numeric";
import { WOPOStatusDef, WOPOStatus, WOFulfillmentStatusDef } from "@/enums/manufacturing";
import { EnumLabel } from "@/components/Enum";
import { useCallback, useEffect, useState } from "react";
import { formatDate } from "@/utils";
import _ from "lodash";

export const WoPickOrderInfo = () => {
    const navigate = useNavigate();
    const params = useParams();
    const [info, setInfo] = useState({});
    const [fulfillments, setFulfillments] = useState([]);

    const back = () => {
        navigate("/workspace/manufacturing/wo-pick-order");
    }

    const getInfo = useCallback(async () => {
        const wopoInfo = await WOPOAPI.get(params.poId);
        const woInfo = await WorkOrderAPI.get(wopoInfo.workOrderId);
        _.forEach(wopoInfo.items, (item) => {
            const rawItem = _.find(woInfo.rawItems, (ri) => ri.id === item.woRawItemId);
            item.woItemQty = rawItem ? rawItem.quantity : 0;
        });
        setInfo(wopoInfo);
    }, [params])

    const getFulfillments = useCallback(async () => {
        try {
            const values = await WoFulfillmentAPI.getByPickOrder(params.poId);
            setFulfillments(values);
        } catch (error) {
            Message.error(error.message);
        }
    }, [params.poId])

    const voidPO = async () => {
        try {
            await WOPOAPI.voidPO(params.poId);
            Message.success("Pick order voided successfully");
            getInfo();
        } catch (error) {
            Message.error(error.message);
        }
    }

    useEffect(() => {
        getInfo();
    }, [getInfo])

    useEffect(() => {
        getFulfillments();
    }, [getFulfillments])

    const totalPickQty = info.pickQty;
    const fulfilledQty = info.fulfilledQty || 0;
    const remainingQty = (totalPickQty || 0) - fulfilledQty;

    const canFulfill = info.status === WOPOStatus.Issued || info.status === WOPOStatus.Partial;
    const canEdit = info.status === WOPOStatus.Issued || info.status === WOPOStatus.Partial;
    const canVoid = info.status === WOPOStatus.Issued || info.status === WOPOStatus.Partial;

    return (
        <Card title={<Space>
            <span>{info.pickOrderNo}</span>
            <Tag><EnumLabel enumObj={WOPOStatusDef} value={info.status} /></Tag>
        </Space>} extra={
            <Space>
                {canFulfill && remainingQty > 0 &&
                    <Button type="primary" onClick={() => navigate(`/workspace/manufacturing/wo-fulfillment/add/${info.workOrderId}?pickOrderIds=${info.id}`)}>Fulfill</Button>
                }
                {canEdit &&
                    <Button type="primary" onClick={() => navigate(`/workspace/manufacturing/wo-pick-order/${params.poId}/edit`)}>Edit</Button>
                }
                {canVoid &&
                    <Popconfirm title="Are you sure you want to void pick order?"
                        onOk={() => voidPO()}>
                        <Button type="primary" status="danger">Void</Button>
                    </Popconfirm>
                }
                <Button onClick={() => back()}>Back</Button>
            </Space>
        }>
            <Descriptions colon=":" column={4} data={[
                { label: "WO#", value: <Link to={`/workspace/manufacturing/work-order/${info.workOrderId}`}>{info.workOrderNo}</Link> },
                { label: "Production Date", value: info.productionDate || "" },
                { label: "Pick Date", value: info.pickDate || "" },
                { label: "BOM Name", value: <Link to={`/workspace/manufacturing/bom/${info.bomId}`}>{info.bomName}</Link> },
                { label: "WO Qty", value: <Number value={info.woQuantity} /> },
                { label: "Pick Qty", value: <Number value={totalPickQty} /> },
                {
                    label: "Fulfillment", value: (
                        <span><Number value={fulfilledQty} /> / <Number value={totalPickQty} /></span>
                    )
                },
                { label: "Warehouse", value: info.warehouseName },
                { label: "Notes", value: info.notes, span: 4 },
            ]} />

            <Table title="Raw Items" rowKey="id" columns={[
                { title: "Item Number", dataIndex: "itemNo" },
                { title: "Item Name", dataIndex: "itemName" },
                { title: "Description", dataIndex: "description" },
                { title: "Pick Qty", render: (col, record) => <Number value={record.pickQty} /> },
                { title: "UOM", dataIndex: "uom" },
                { title: "Warehouse Location", dataIndex: "locationName" },
                { title: "Lot#", dataIndex: "lot" },
                { title: "Expiration", dataIndex: "expiredDate" },
            ]}
                data={info.items}
            />

            {fulfillments.length > 0 && (
                <>
                    <div style={{ marginTop: 24, marginBottom: 16 }}>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>Fulfillments</span>
                    </div>
                    <Table rowKey="id" columns={[
                        {
                            title: "Fulfillment Order#",
                            dataIndex: "fulfillmentNo",
                            render: (col, record) =>
                                <Link to={`/workspace/manufacturing/wo-fulfillment/${record.id}`}>{record.fulfillmentNo}</Link>
                        },
                        { title: "Fulfillment Date", dataIndex: "fulfillDate", render: (val) => val ? formatDate(val) : "" },
                        { title: "Qty", dataIndex: "pickOrderQty", render: (val) => <Number value={val} /> },
                        { title: "Status", dataIndex: "status", render: (col, record) => <EnumLabel enumObj={WOFulfillmentStatusDef} value={record.status} /> },
                    ]} data={fulfillments} pagination={false} />
                </>
            )}
        </Card>
    )
}
